/**
 * #670 Positions panel. Outcome positions first (they ARE the "+<enc>" spot balances, presented as
 * positions), then main-dex perp positions and the perp account margin summary.
 * #873: the outcome positions can be sorted (remembered per device), and the own account gets a "Close" button per
 * position that opens the market's ticket with a market sell of the free shares filled in.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OutcomeHoldingsTable } from './BalancesPanel';
import type { OutcomeHolding } from './derive';
import {
  loadPositionSort,
  POSITION_SORT_LABEL,
  POSITION_SORTS,
  parsePositionSort,
  savePositionSort,
  sortPositions,
  type PositionSort,
} from './positionSort';
import { derivePerps } from './derive';
import { fmtNum, fmtPct, fmtPx, fmtSignedUsd, fmtUsd, pnlClass } from './format';
import { marketHref, useDerivedBalances } from './useDerivedBalances';
import { usePerpPositions } from './usePerpPositions';
import OtherVenuesToggle from '../venue/OtherVenuesToggle';
import { venueLabel } from '../venue/scope';
import { useVenueOutcomeRows } from '../venue/useVenueRows';
import { useT } from '../i18n/useT';
import { requestClose } from '../market/ticketPrefill';
import { useViewedAccount } from '../wallet/context';
import { useGeo } from '../geo/context';

function SortSelect({ value, onChange }: { value: PositionSort; onChange: (s: PositionSort) => void }) {
  const t = useT();
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted">
      {t('positions.sort.label')}
      <select
        value={value}
        onChange={(e) => onChange(parsePositionSort(e.target.value))}
        className="rounded-md border border-border bg-bg px-1.5 py-1 text-xs text-fg"
        data-testid="positions-sort"
      >
        {POSITION_SORTS.map((s) => (
          <option key={s} value={s}>
            {t(POSITION_SORT_LABEL[s])}
          </option>
        ))}
      </select>
    </label>
  );
}

function SummaryItem({ label, value, testId, className = '' }: { label: string; value: string; testId?: string; className?: string }) {
  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className={`num ${className}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export default function PositionsPanel({ address }: { address: string }) {
  const bal = useDerivedBalances(address);
  const perp = usePerpPositions(address);
  const { positions, summary } = derivePerps(perp.data);
  const outcomes = bal.derived.outcomes;
  const scoped = useVenueOutcomeRows(outcomes);
  const t = useT();
  const navigate = useNavigate();
  const viewed = useViewedAccount();
  const [sort, setSort] = useState<PositionSort>(() => loadPositionSort());
  const changeSort = (s: PositionSort) => {
    setSort(s);
    savePositionSort(s);
  };
  const rows = sortPositions(scoped.visible, sort);
  // Own account only: a ghosted account cannot be traded (the ticket would refuse anyway). #769 (D5): hidden while
  // browse-only applies, because a close is an order; cancels stay available elsewhere.
  const geo = useGeo();
  const onClose = viewed.canTrade && geo.canTrade
    ? (r: OutcomeHolding) => {
        requestClose({ outcome: r.outcome, side: r.side });
        navigate(marketHref(r.outcome, r.side, null));
      }
    : undefined;

  return (
    <section className="space-y-4" aria-label={t('portfolio.tab.positions')} data-testid="positions-panel">
      <div className="card p-4">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h2 className="font-semibold">{t('positions.outcome')}</h2>
          {outcomes.length > 0 && (
            <span className="num text-sm text-muted">
              {fmtUsd(bal.derived.outcomeValue)}{' '}
              <span className={pnlClass(bal.derived.outcomeUpnl)}>{fmtSignedUsd(bal.derived.outcomeUpnl)}</span>
            </span>
          )}
        </div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 empty:hidden">
          <OtherVenuesToggle v={scoped.v} hidden={scoped.hidden} />
          {outcomes.length > 0 && <SortSelect value={sort} onChange={changeSort} />}
        </div>
        {bal.isLoading ? (
          <p className="text-sm text-muted">{t('common.loading')}</p>
        ) : scoped.visible.length === 0 ? (
          <p className="text-sm text-muted" data-testid="no-outcome-positions">
            {outcomes.length === 0 ? t('positions.no_outcome') : t('positions.no_outcome_venue', { venue: venueLabel(scoped.v.venue) })}
          </p>
        ) : (
          <OutcomeHoldingsTable rows={rows} testId="outcome-positions" onClose={onClose} />
        )}
      </div>

      <div className="card p-4">
        <h2 className="mb-2 font-semibold">
          {t('positions.perp')} <span className="text-sm font-normal text-muted">{t('positions.main_dex')}</span>
        </h2>
        {perp.error && !perp.isLoading && (
          <p className="mb-2 text-sm text-danger">{t('positions.perp_error', { error: perp.error.message })}</p>
        )}
        {summary && (
          <div className="mb-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-5" data-testid="margin-summary">
            <SummaryItem label={t('positions.account_value')} value={fmtUsd(summary.accountValue)} testId="perp-account-value" />
            <SummaryItem label={t('positions.margin_used')} value={fmtUsd(summary.totalMarginUsed)} testId="perp-margin-used" />
            <SummaryItem label={t('positions.maintenance')} value={fmtUsd(summary.maintenanceMargin)} />
            <SummaryItem label={t('positions.withdrawable')} value={fmtUsd(summary.withdrawable)} testId="perp-withdrawable" />
            <SummaryItem
              label={t('positions.upnl')}
              value={fmtSignedUsd(summary.upnl)}
              className={pnlClass(summary.upnl)}
              testId="perp-upnl"
            />
          </div>
        )}
        {perp.isLoading ? (
          <p className="text-sm text-muted">{t('common.loading')}</p>
        ) : positions.length === 0 ? (
          <p className="text-sm text-muted" data-testid="no-perp-positions">
            {t('positions.no_perp')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm" data-testid="perp-positions">
              <thead className="text-start text-xs text-muted">
                <tr>
                  <th className="py-2 pe-3 font-normal">{t('positions.col.coin')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('positions.col.size')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('positions.col.value')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('positions.col.entry')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('positions.col.mark')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('positions.col.upnl_roe')}</th>
                  <th className="py-2 pe-3 text-end font-normal">{t('positions.col.leverage')}</th>
                  <th className="py-2 text-end font-normal">{t('positions.col.liq')}</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => (
                  <tr key={p.coin} className="border-t border-border" data-testid={`perp-row-${p.coin}`}>
                    <td className="py-2 pe-3">
                      <span className={p.size > 0 ? 'text-yes' : 'text-no'} translate="no">{p.coin}</span>
                    </td>
                    <td className={`num py-2 pe-3 text-end ${p.size > 0 ? 'text-yes' : 'text-no'}`}>
                      {fmtNum(p.size, 6)}
                    </td>
                    <td className="num py-2 pe-3 text-end">{fmtUsd(p.positionValue)}</td>
                    <td className="num py-2 pe-3 text-end">{fmtPx(p.entryPx)}</td>
                    <td className="num py-2 pe-3 text-end">{fmtPx(p.markPx)}</td>
                    <td className={`num py-2 pe-3 text-end ${pnlClass(p.upnl)}`}>
                      {fmtSignedUsd(p.upnl)} <span className="text-xs">({fmtPct(p.roe)})</span>
                    </td>
                    <td className="num py-2 pe-3 text-end">
                      {p.leverage}x <span className="text-xs text-muted" translate="no">{p.leverageType}</span>
                    </td>
                    <td className="num py-2 text-end">{fmtPx(p.liqPx)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
